import { AllExtraServicesSelectionCard, Tile } from "@organisms";
import { cn } from "@utils";
import { useTranslation } from "react-i18next";
import { useBreakPoint, useModal } from "@hooks";
import { ComponentProps, PropsWithChildren } from "react";
import { ExtrasLoader } from "./ExtrasLoader";
import { useGeneralExtraServices } from "@lib/logic";
import { trackEvent } from "@lib/infrastructure";
import { ExtraServicesListItem } from "@molecules";

type Props = {
  referenceDate: Date;
};

export const ExtraServicesOverview = ({ referenceDate }: Props) => {
  const bp = useBreakPoint();
  const { t } = useTranslation();
  const { addPages, openModal } = useModal();

  const selectedDateString = referenceDate.toISOString();

  const { highlightedServices, highlightedServicesCount, totalServicesCount, loadingStatus } =
    useGeneralExtraServices(selectedDateString);
  const loading = loadingStatus !== "Loaded";

  // Hide when loaded & empty
  if (!loading && highlightedServicesCount === 0) return <></>;

  const viewAllAction = () => {
    trackEvent("Home_ExtraServicesTile__ViewAll");
    addPages([
      <AllExtraServicesSelectionCard
        category="General"
        selectedDate={referenceDate.toISOString()}
        locationName={undefined}
        meetingId={undefined}
      />,
    ]);
    openModal();
  };

  return (
    <TileWrapper
      viewAllAction={highlightedServicesCount < totalServicesCount ? viewAllAction : undefined}
      title={t("translation:ServicesAt", { officeName: highlightedServices.buildingName })}>
      {loading ? (
        <ExtrasLoader />
      ) : (
        <div className={cn("gap-6 w-full grid grid-cols-1", { "grid-cols-2": bp === "large" })}>
          {highlightedServices.data.map(service => {
            return (
              <ExtraServicesListItem
                key={service.id}
                service={service}
                selectedDate={referenceDate.toISOString()}
                locationName={undefined}
                meetingId={undefined}
              />
            );
          })}
        </div>
      )}
    </TileWrapper>
  );
};

type WrapperProps = PropsWithChildren<Pick<ComponentProps<typeof Tile>, "viewAllAction"> & { title: string }>;

const TileWrapper = ({ title, children, viewAllAction }: WrapperProps) => {
  return (
    <Tile
      viewAllAction={viewAllAction}
      title={title}>
      {children}
    </Tile>
  );
};
